<template>
  <div class="customer-profile">
    <!-- Header -->
    <div class="cp-header">
      <div class="cp-header__left">
        <button class="btn-back" @click="$router.push({ name: 'customers' })">
          <i class="fa-solid fa-arrow-left"></i>
        </button>
        <div>
          <span class="header-eyebrow">Perfil de customer</span>
          <h2 class="header-title">
            {{ customer ? [customer.nombre, customer.apellidos].filter(Boolean).join(' ') || customer.companyName : '...' }}
          </h2>
          <span class="header-company" v-if="customer && customer.companyName">{{ customer.companyName }}</span>
        </div>
      </div>
      <div class="cp-header__right">
        <b-button variant="outline-primary" size="sm" @click="modalStatement = true">
          <i class="fa-solid fa-file-lines mr-25"></i> Statement
        </b-button>
        <span
          v-if="customer && customer.empresa"
          class="badge-empresa"
          :class="customer.empresa === 'Harber London' ? 'badge-empresa--hb' : 'badge-empresa--cl'"
        >
          {{ customer.empresa }}
        </span>
      </div>
    </div>

    <!-- Modal: statement con rango de fechas -->
    <b-modal
      v-model="modalStatement"
      title="Statement del cliente"
      centered
      :ok-disabled="generandoStatement"
      ok-title="Generar PDF"
      cancel-title="Cancelar"
      @ok.prevent="generarStatement"
    >
      <p class="text-muted mb-1">
        Resumen del estado de cuenta: cargos de proformas, pagos recibidos y saldo pendiente.
      </p>
      <label class="small font-weight-bold">Pedido</label>
      <b-form-select v-model="statementPedidoId" :options="statementPedidoOptions" size="sm" class="mb-1" />
      <b-row v-if="!statementPedidoId">
        <b-col cols="6">
          <label class="small font-weight-bold">Desde</label>
          <b-form-input v-model="statementDesde" type="date" size="sm" />
          <small class="text-muted">Vacío = todo el histórico</small>
        </b-col>
        <b-col cols="6">
          <label class="small font-weight-bold">Hasta</label>
          <b-form-input v-model="statementHasta" type="date" size="sm" />
        </b-col>
      </b-row>
      <div v-if="generandoStatement" class="text-center mt-1">
        <b-spinner small class="align-middle mr-50" /> Generando PDF…
      </div>

      <!-- Historial: cada statement generado queda archivado con su número -->
      <template v-if="statementsPrevios.length">
        <hr class="my-1" />
        <label class="small font-weight-bold d-block mb-25">Statements anteriores</label>
        <div class="st-lista">
          <div v-for="st in statementsPrevios" :key="st.id" class="st-item">
            <div class="st-item__info">
              <span class="font-weight-bold">{{ st.numero }}</span>
              <small class="text-muted d-block">
                {{ formatFecha(st.createdAt).split(',')[0] }}
                · {{ st.pedidoRef || 'Todos los pedidos' }}
                · {{ st.simbolo }}{{ st.outstanding }} pendiente
              </small>
            </div>
            <b-button
              size="sm"
              variant="flat-primary"
              class="btn-icon"
              title="Ver PDF archivado"
              @click="verStatementArchivado(st)"
            >
              <i class="fa-solid fa-file-pdf"></i>
            </b-button>
          </div>
        </div>
      </template>
    </b-modal>

    <!-- Datos del cliente (plegado por defecto) -->
    <div class="panel-card panel-card--datos" v-if="customer">
      <button class="panel-card__header panel-card__header--toggle" @click="mostrarDatos = !mostrarDatos">
        <h3 class="panel-card__title"><i class="fa-solid fa-user mr-50"></i> Datos Cliente</h3>
        <i :class="mostrarDatos ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="toggle-chevron"></i>
      </button>
      <div class="detail-list detail-list--grid" v-if="mostrarDatos">
        <div class="detail-item" v-for="d in datosCliente" :key="d.label">
          <span class="detail-item__icon"><i :class="d.icon"></i></span>
          <div>
            <span class="detail-item__label">{{ d.label }}</span>
            <span class="detail-item__value">{{ d.value }}</span>
          </div>
        </div>
        <div class="detail-item" v-if="customer.envioActivo == 1 && customer.envioDireccion">
          <span class="detail-item__icon"><i class="fa-solid fa-truck"></i></span>
          <div>
            <span class="detail-item__label">Direccion de envio</span>
            <span class="detail-item__value">{{ customer.envioNombre }}<template v-if="customer.envioEmpresa"> ({{ customer.envioEmpresa }})</template></span>
            <span class="detail-item__value">{{ [customer.envioDireccion, customer.envioCp, customer.envioCiudad, customer.envioPais].filter(Boolean).join(', ') }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- Acceso a la web wholesale B2B -->
    <div class="panel-card panel-card--datos" v-if="customer">
      <button class="panel-card__header panel-card__header--toggle" @click="mostrarB2b = !mostrarB2b">
        <h3 class="panel-card__title">
          <i class="fa-solid fa-globe mr-50"></i> Acceso web B2B
          <span v-if="b2b" class="badge-estado ml-50" :class="b2b.activo ? 'badge-estado--ok' : 'badge-estado--no'">
            {{ b2b.activo ? 'Activo' : 'Sin acceso' }}
          </span>
        </h3>
        <i :class="mostrarB2b ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'" class="toggle-chevron"></i>
      </button>
      <div v-if="mostrarB2b" class="b2b-body">
        <div v-if="!b2b" class="text-muted"><b-spinner small class="mr-50" /> Cargando…</div>
        <template v-else>
          <div class="b2b-fila">
            <b-form-checkbox v-model="b2b.activo" switch @change="guardarB2b">
              <span class="font-weight-bold">Puede entrar en la web wholesale</span>
            </b-form-checkbox>
            <div class="b2b-wsp">
              <label class="small font-weight-bold mb-0 mr-50">% Descuento</label>
              <b-form-input
                v-model="b2bDescuento"
                type="number"
                min="0"
                max="99"
                size="sm"
                style="width: 80px"
                @change="guardarB2b"
              />
              <small class="text-muted ml-50">sobre el RRP · 60 = tarifa estándar</small>
            </div>
          </div>

          <div v-if="b2b.activo" class="b2b-fila mt-1">
            <div>
              <span class="badge-estado" :class="b2b.tienePassword ? 'badge-estado--ok' : 'badge-estado--warn'">
                {{ b2b.tienePassword ? 'Contraseña configurada' : 'Sin contraseña todavía' }}
              </span>
              <small
                v-if="b2b.ultimoLogin"
                class="text-muted ml-50"
                title="Fecha del último inicio de sesión con email y contraseña (la sesión dura 30 días, puede haber usado la web después sin volver a loguearse)"
              >Último login: {{ formatFecha(b2b.ultimoLogin) }}</small>
            </div>
            <div class="b2b-acciones">
              <b-button size="sm" variant="outline-primary" :disabled="!!generandoInvite" @click="generarInvitacion(false)">
                <b-spinner v-if="generandoInvite === 'link'" small class="mr-25" />
                <i v-else class="fa-solid fa-link mr-25"></i>
                {{ b2b.tienePassword ? 'Enlace para resetear contraseña' : 'Generar enlace de invitación' }}
              </b-button>
              <b-button size="sm" variant="primary" :disabled="!!generandoInvite || !b2b.email" @click="confirmarEnvioInvitacion">
                <b-spinner v-if="generandoInvite === 'email'" small class="mr-25" />
                <i v-else class="fa-solid fa-envelope mr-25"></i>
                {{ b2b.tienePassword ? 'Enviar reset por email' : 'Enviar invitación por email' }}
              </b-button>
            </div>
          </div>

          <div v-if="inviteLink" class="b2b-invite mt-1">
            <code class="b2b-invite__link">{{ inviteLink }}</code>
            <b-button size="sm" variant="flat-primary" class="btn-icon" title="Copiar" @click="copiarInvite">
              <i class="fa-solid fa-copy"></i>
            </b-button>
            <small class="text-muted d-block mt-25">
              Envíaselo al cliente ({{ b2b.email || 'sin email' }}). Caduca en 7 días.
            </small>
          </div>
        </template>
      </div>
    </div>

    <!-- KPI Strip -->
    <div class="kpi-strip">
      <div class="kpi-card">
        <span class="kpi-label">Pedidos</span>
        <span class="kpi-value">{{ pedidosFiltrados.length }}</span>
        <span class="kpi-foot">{{ anioFiltro === 'Todos' ? 'en total' : anioFiltro }}</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Unidades</span>
        <span class="kpi-value">{{ totalUnidades }}</span>
        <span class="kpi-foot">productos</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Facturado</span>
        <span class="kpi-value">{{ totalFacturado || '--' }}</span>
        <span class="kpi-foot">WSP</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Por pagar</span>
        <span class="kpi-value" :class="totalPorPagar ? 'text-warning' : ''">{{ totalPorPagar || '--' }}</span>
        <span class="kpi-foot">{{ totalPorPagar ? 'pendiente de cobro' : 'al día' }}</span>
      </div>
      <div class="kpi-card">
        <span class="kpi-label">Ultimo pedido</span>
        <span class="kpi-value kpi-value--sm">{{ ultimoPedido || '--' }}</span>
        <span class="kpi-foot">fecha</span>
      </div>
    </div>

    <div class="cp-grid">
      <!-- Historial de pedidos -->
      <div class="panel-card panel-card--pedidos">
        <div class="panel-card__header">
          <h3 class="panel-card__title"><i class="fa-solid fa-clock-rotate-left mr-50"></i> Historial de pedidos</h3>
          <div class="filter-pills" v-if="anios.length">
            <button
              class="pill"
              :class="{ active: anioFiltro === 'Todos' }"
              @click="anioFiltro = 'Todos'"
            >Todos</button>
            <button
              v-for="a in anios"
              :key="a"
              class="pill"
              :class="{ active: anioFiltro === a }"
              @click="anioFiltro = a"
            >{{ a }}</button>
          </div>
          <span class="footer-count">{{ pedidosFiltrados.length }} pedidos</span>
        </div>

        <b-table
          :items="pedidosFiltrados"
          :fields="columnas"
          class="mb-0"
          hover
          responsive
          show-empty
          :empty-text="cargando ? 'Cargando...' : 'Este customer no tiene pedidos todavia'"
          thead-class="table-head"
          tbody-tr-class="table-row"
        >
          <template #cell(createdAt)="data">
            <span class="cell-mono">{{ formatFecha(data.item.createdAt) }}</span>
          </template>

          <template #cell(uds)="data">
            <span class="cell-mono">{{ totalUds(data.item) }}</span>
          </template>

          <template #cell(total)="data">
            <span class="cell-mono cell-total">{{ data.item.simbolo }}{{ data.item.total }}</span>
            <span class="cell-descuento" v-if="Number(data.item.descuentoPct)">-{{ data.item.descuentoPct }}%</span>
          </template>

          <template #cell(pago)="data">
            <span
              class="badge-estado badge-estado--click"
              :class="estadoPago(data.item).clase"
              @click="$refs.pagosModal.abrir(data.item)"
            >
              {{ estadoPago(data.item).text }}
            </span>
            <span class="cell-mono cell-pago-detalle" v-if="Number(data.item.pagado)">
              {{ data.item.simbolo }}{{ data.item.pagado }} / {{ data.item.simbolo }}{{ data.item.total }}
            </span>
          </template>

          <template #cell(emailEnviado)="data">
            <span class="badge-estado" :class="data.item.emailEnviado ? 'badge-estado--ok' : 'badge-estado--no'">
              {{ data.item.emailEnviado ? 'Enviado' : 'No enviado' }}
            </span>
          </template>

          <template #cell(acciones)="data">
            <div class="cell-actions">
              <button
                class="action-btn action-btn--view"
                title="Ver items"
                @click="data.toggleDetails()"
              >
                <i :class="data.detailsShowing ? 'fa-solid fa-chevron-up' : 'fa-solid fa-chevron-down'"></i>
              </button>
              <button
                class="action-btn action-btn--proforma"
                title="Factura proforma"
                @click="$refs.proformaModal.abrir(data.item)"
              >
                <i class="fa-solid fa-file-invoice"></i>
              </button>
              <button
                class="action-btn action-btn--pdf"
                title="Ver PDF"
                :disabled="pdfPedidoId === data.item.id"
                @click="verPdf(data.item)"
              >
                <b-spinner v-if="pdfPedidoId === data.item.id" small />
                <i v-else class="fa-solid fa-file-pdf"></i>
              </button>
            </div>
          </template>

          <template #row-details="data">
            <div class="pedido-items">
              <div class="pedido-items__row" v-for="(it, i) in data.item.items" :key="i">
                <img v-if="it.image" :src="it.image" class="pedido-items__img" />
                <div class="pedido-items__info">
                  <span class="pedido-items__title">{{ it.title }}</span>
                  <span class="pedido-items__sku">{{ it.sku }}</span>
                </div>
                <span class="cell-mono">x{{ it.qty }}</span>
                <span class="cell-mono cell-total">{{ data.item.simbolo }}{{ (Number(it.wspUnit) || 0) * (Number(it.qty) || 0) }}</span>
              </div>
            </div>
          </template>
        </b-table>
      </div>
    </div>

    <pagos-pedido-modal ref="pagosModal" />
    <proforma-modal ref="proformaModal" />
  </div>
</template>

<script>
import { BTable, BSpinner } from 'bootstrap-vue'
import axios from '@axios'
import PagosPedidoModal from '@/components/PagosPedidoModal.vue'
import ProformaModal from '@/components/ProformaModal.vue'

export default {
  components: {
    BTable, BSpinner, PagosPedidoModal, ProformaModal,
  },
  data() {
    return {
      customer: null,
      pedidos: [],
      anioFiltro: 'Todos',
      mostrarDatos: false,
      mostrarB2b: false,
      b2b: null,
      // % de descuento sobre RRP que se muestra en pantalla (el API guarda wspPct = 100 - descuento)
      b2bDescuento: null,
      inviteLink: '',
      generandoInvite: false,
      b2bActivoPrevio: false,
      cargando: true,
      pdfPedidoId: null,
      modalStatement: false,
      statementsPrevios: [],
      statementPedidoId: null,
      statementDesde: '',
      statementHasta: new Date().toISOString().slice(0, 10),
      generandoStatement: false,
      columnas: [
        { key: 'ref', label: 'Pedido', sortable: true },
        { key: 'createdAt', label: 'Fecha', sortable: true },
        { key: 'uds', label: 'Uds' },
        { key: 'total', label: 'Total', sortable: true },
        { key: 'pago', label: 'Pago' },
        { key: 'emailEnviado', label: 'Email' },
        { key: 'acciones', label: '', thStyle: { width: '100px' } },
      ],
    }
  },
  computed: {
    // Opciones del statement: todos (rango de fechas) o un pedido concreto
    statementPedidoOptions() {
      return [
        { value: null, text: 'Todos los pedidos — por rango de fechas' },
        ...this.pedidos.map(p => ({
          value: p.id,
          text: `${p.ref} — ${this.formatFecha(p.createdAt).split(',')[0]} — ${p.simbolo}${p.total}`,
        })),
      ]
    },
    datosCliente() {
      const c = this.customer
      if (!c) return []
      return [
        { label: 'Email', value: c.email, icon: 'fa-solid fa-envelope' },
        { label: 'Telefono', value: c.telefono, icon: 'fa-solid fa-phone' },
        { label: 'NIF', value: c.nif, icon: 'fa-solid fa-id-card' },
        { label: 'VAT', value: c.vat, icon: 'fa-solid fa-percent' },
        { label: 'Direccion', value: [c.direccion, c.cp, c.ciudad, c.pais].filter(Boolean).join(', '), icon: 'fa-solid fa-location-dot' },
        { label: 'Web', value: c.web, icon: 'fa-solid fa-link' },
        { label: 'Social media', value: c.redes, icon: 'fa-solid fa-share-nodes' },
        { label: 'Notas', value: c.notas, icon: 'fa-solid fa-note-sticky' },
        { label: 'Observaciones', value: c.observaciones, icon: 'fa-solid fa-comment' },
      ].filter(d => d.value)
    },
    anios() {
      const set = new Set()
      this.pedidos.forEach(p => {
        const d = new Date(p.createdAt)
        if (!Number.isNaN(d.getTime())) set.add(d.getFullYear())
      })
      return Array.from(set).sort((a, b) => b - a)
    },
    pedidosFiltrados() {
      if (this.anioFiltro === 'Todos') return this.pedidos
      return this.pedidos.filter(p => new Date(p.createdAt).getFullYear() === this.anioFiltro)
    },
    totalUnidades() {
      return this.pedidosFiltrados.reduce((acc, p) => acc + this.totalUds(p), 0)
    },
    // Suma de totales agrupada por moneda (ej. "£1200 + €300")
    totalFacturado() {
      const porMoneda = {}
      this.pedidosFiltrados.forEach(p => {
        const s = p.simbolo || '£'
        porMoneda[s] = (porMoneda[s] || 0) + (Number(p.total) || 0)
      })
      return Object.keys(porMoneda).map(s => `${s}${porMoneda[s]}`).join(' + ')
    },
    ultimoPedido() {
      if (!this.pedidosFiltrados.length) return ''
      return this.formatFecha(this.pedidosFiltrados[0].createdAt).split(',')[0]
    },
    // Pendiente de cobro (total - pagado), agrupado por moneda
    totalPorPagar() {
      const porMoneda = {}
      this.pedidosFiltrados.forEach(p => {
        const s = p.simbolo || '£'
        const resto = (Number(p.total) || 0) - (Number(p.pagado) || 0)
        if (resto > 0.009) porMoneda[s] = (porMoneda[s] || 0) + resto
      })
      return Object.keys(porMoneda)
        .map(s => `${s}${Math.round(porMoneda[s] * 100) / 100}`)
        .join(' + ')
    },
  },
  methods: {
    formatFecha(f) {
      if (!f) return ''
      const d = new Date(f)
      return Number.isNaN(d.getTime()) ? f : d.toLocaleString('es-ES')
    },
    totalUds(pedido) {
      return (pedido.items || []).reduce((acc, it) => acc + (Number(it.qty) || 0), 0)
    },
    estadoPago(pedido) {
      const total = Number(pedido.total) || 0
      const pagado = Number(pedido.pagado) || 0
      if (pagado <= 0) return { text: 'Pendiente', clase: 'badge-estado--no' }
      if (pagado + 0.009 >= total) return { text: 'Pagado', clase: 'badge-estado--ok' }
      return { text: 'Parcial', clase: 'badge-estado--warn' }
    },
    verPdf(pedido) {
      this.pdfPedidoId = pedido.id
      axios
        .get(`/escaner/pedidos/${pedido.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo generar el PDF', { title: 'Error', variant: 'danger', solidColor: true })
        })
        .finally(() => { this.pdfPedidoId = null })
    },
    cargarStatements() {
      axios
        .get(`/customers/${this.$route.params.id}/statements`)
        .then(({ data }) => { this.statementsPrevios = data || [] })
        .catch(() => {})
    },
    verStatementArchivado(st) {
      axios
        .get(`/customers/statements/${st.id}/pdf`, { responseType: 'blob' })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo abrir el statement', { title: 'Error', variant: 'danger', solidColor: true })
        })
    },
    generarStatement() {
      this.generandoStatement = true
      const pedidoId = this.statementPedidoId || undefined
      axios
        .get(`/customers/${this.$route.params.id}/statement/pdf`, {
          responseType: 'blob',
          params: {
            pedidoId,
            desde: pedidoId ? undefined : (this.statementDesde || undefined),
            hasta: pedidoId ? undefined : (this.statementHasta || undefined),
          },
        })
        .then(({ data }) => {
          const url = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
          window.open(url, '_blank')
          this.modalStatement = false
          this.cargarStatements()
        })
        .catch(() => {
          this.$bvToast.toast('No se pudo generar el statement', { title: 'Error', variant: 'danger', solidColor: true })
        })
        .finally(() => { this.generandoStatement = false })
    },
    cargarB2b() {
      axios
        .get(`/customers/${this.$route.params.id}/b2b`)
        .then(({ data }) => {
          this.b2b = data
          this.b2bActivoPrevio = !!data.activo
          this.b2bDescuento = Math.round((100 - (Number(data.wspPct) || 40)) * 100) / 100
        })
        .catch(() => {})
    },
    guardarB2b() {
      // El @change del switch/input llega antes de actualizar el modelo: esperar un tick
      this.$nextTick(() => {
        // Descuento escrito en pantalla -> wspPct del API. Si está vacío o no
        // es válido, se mantiene el valor actual y se repinta.
        let d = parseFloat(this.b2bDescuento)
        if (isFinite(d)) {
          d = Math.min(99, Math.max(0, d))
          this.b2b.wspPct = Math.round((100 - d) * 100) / 100
        }
        this.b2bDescuento = Math.round((100 - (Number(this.b2b.wspPct) || 40)) * 100) / 100

        axios
          .put(`/customers/${this.$route.params.id}/b2b`, { activo: this.b2b.activo, wspPct: this.b2b.wspPct })
          .then(({ data }) => {
            const recienActivado = data.activo && !this.b2bActivoPrevio
            this.b2bActivoPrevio = !!data.activo
            this.b2b.wspPct = data.wspPct
            this.b2bDescuento = Math.round((100 - (Number(data.wspPct) || 40)) * 100) / 100
            this.$bvToast.toast('Acceso B2B guardado', { title: 'Cliente', variant: 'success', solidColor: true })
            // Al activar el acceso de un cliente sin cuenta, ofrecer mandarle la invitación
            if (recienActivado && !this.b2b.tienePassword) this.confirmarEnvioInvitacion()
          })
          .catch(e => {
            const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo guardar'
            this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solidColor: true })
            this.cargarB2b()
          })
      })
    },
    confirmarEnvioInvitacion() {
      const reset = this.b2b.tienePassword
      this.$swal({
        icon: 'question',
        title: reset ? 'Enviar reset de contraseña' : 'Enviar invitación',
        html: `¿Mandar un email a <b>${this.b2b.email}</b> con el enlace para ${reset ? 'resetear su contraseña' : 'crear su cuenta'} en la web wholesale?`,
        showCancelButton: true,
        confirmButtonText: 'Sí, enviar',
        cancelButtonText: 'No',
        confirmButtonColor: '#28c76f',
      }).then(result => {
        if (result.isConfirmed) this.generarInvitacion(true)
      })
    },
    generarInvitacion(enviarEmail) {
      this.generandoInvite = enviarEmail ? 'email' : 'link'
      axios
        .post(`/customers/${this.$route.params.id}/b2b/invitacion`, { enviarEmail: !!enviarEmail })
        .then(({ data }) => {
          this.inviteLink = data.link
          this.b2b.invitePendiente = true
          if (data.emailEnviado) {
            this.$bvToast.toast(`Email enviado a ${data.email}`, { title: 'Cliente', variant: 'success', solidColor: true })
          } else if (data.emailError) {
            this.$bvToast.toast(`${data.emailError}. Copia el enlace y envíaselo a mano.`, { title: 'Error', variant: 'danger', solidColor: true })
          }
        })
        .catch(e => {
          const msg = (e.response && e.response.data && e.response.data.msg) || 'No se pudo generar la invitación'
          this.$bvToast.toast(msg, { title: 'Error', variant: 'danger', solidColor: true })
        })
        .finally(() => { this.generandoInvite = false })
    },
    copiarInvite() {
      navigator.clipboard.writeText(this.inviteLink).then(() => {
        this.$bvToast.toast('Enlace copiado', { title: 'Cliente', variant: 'success', solidColor: true })
      })
    },
    load() {
      const { id } = this.$route.params
      axios
        .get('/customers')
        .then(({ data }) => {
          this.customer = (data || []).find(c => String(c.id) === String(id)) || null
          return axios.get('/escaner/pedidos', {
            params: {
              customerId: id,
              email: (this.customer && this.customer.email) || undefined,
            },
          })
        })
        .then(({ data }) => { this.pedidos = data || [] })
        .catch(() => {})
        .finally(() => { this.cargando = false })
    },
  },
  watch: {
    modalStatement(v) {
      if (v) this.cargarStatements()
    },
  },
  mounted() {
    axios.post('/uso', { seccion: 'customer-profile' }).catch(() => {})
    this.load()
    this.cargarB2b()
  },
}
</script>

<style lang="scss" scoped>
$primary: #3B82F6;
$success: #10B981;
$danger: #EF4444;
$border: #E2E8F0;
$text: #1E293B;
$text-muted: #64748B;
$bg: #F8FAFC;
$card-bg: #FFFFFF;
$radius: 12px;
$radius-sm: 8px;
$shadow: 0 1px 3px rgba(0,0,0,0.06), 0 1px 2px rgba(0,0,0,0.04);

.customer-profile {
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 8px;
}

.cp-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;

  &__right {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  &__left {
    display: flex;
    align-items: center;
    gap: 14px;
  }
}

.btn-back {
  width: 38px;
  height: 38px;
  border-radius: $radius-sm;
  border: 1px solid $border;
  background: $card-bg;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover { color: $primary; border-color: $primary; }
}

.header-eyebrow {
  display: block;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 1.5px;
  text-transform: uppercase;
  color: $primary;
  margin-bottom: 2px;
}

.header-title {
  font-family: 'Plus Jakarta Sans', sans-serif;
  font-size: 24px;
  font-weight: 700;
  color: $text;
  margin: 0;
}

.header-company {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
}

.badge-empresa {
  padding: 5px 14px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;

  &--hb { background: rgba($primary, 0.1); color: $primary; }
  &--cl { background: rgba($success, 0.1); color: $success; }
}

.kpi-strip {
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  gap: 12px;
  margin-bottom: 20px;
}

.kpi-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius-sm;
  padding: 16px 18px;
  display: flex;
  flex-direction: column;
  box-shadow: $shadow;
}

.kpi-label {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
}

.kpi-value {
  font-family: 'JetBrains Mono', monospace;
  font-size: 22px;
  font-weight: 700;
  color: $text;
  font-feature-settings: "tnum";
  margin: 4px 0 2px;

  &--sm { font-size: 15px; margin-top: 9px; }
}

.kpi-foot {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $text-muted;
}

.cp-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.panel-card {
  background: $card-bg;
  border: 1px solid $border;
  border-radius: $radius;
  box-shadow: $shadow;
  overflow: hidden;

  &__header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 16px 20px;
    border-bottom: 1px solid $border;
  }

  &__title {
    font-family: 'Plus Jakarta Sans', sans-serif;
    font-size: 15px;
    font-weight: 700;
    color: $text;
    margin: 0;
  }

  &--datos {
    margin-bottom: 20px;
  }

  &__header--toggle {
    width: 100%;
    background: transparent;
    border: none;
    border-bottom: none;
    cursor: pointer;
    text-align: left;
  }
}

.toggle-chevron {
  font-size: 12px;
  color: $text-muted;
}

// Historial de statements (modal)
.st-lista {
  max-height: 220px;
  overflow-y: auto;
}

.st-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 2px;
  border-bottom: 1px solid $border;

  &:last-child { border-bottom: none; }

  &__info { min-width: 0; }
}

// Acceso web B2B
.b2b-body {
  padding: 14px 20px 18px;
  border-top: 1px solid $border;
}

.b2b-fila {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 10px;
}

.b2b-acciones {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.b2b-wsp {
  display: flex;
  align-items: center;
}

.b2b-invite {
  background: $bg;
  border: 1px dashed $border;
  border-radius: $radius-sm;
  padding: 10px 12px;

  &__link {
    font-size: 11px;
    word-break: break-all;
  }
}

.detail-list {
  padding: 10px;

  &--grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    border-top: 1px solid $border;
  }
}

.detail-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 10px 12px;
  border-radius: 8px;

  &:hover { background: $bg; }

  &__icon {
    width: 28px;
    height: 28px;
    border-radius: 6px;
    background: #F1F5F9;
    color: $text-muted;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    flex-shrink: 0;
  }

  &__label {
    display: block;
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    color: #94A3B8;
    margin-bottom: 3px;
  }

  &__value {
    display: block;
    font-size: 13px;
    font-weight: 500;
    color: $text;
    word-break: break-word;
  }
}

// Los ::v-deep van bajo .customer-profile a proposito: en la raiz, postcss-rtl + scoped generan [dir][data-v-xxx]
// y ninguna propiedad direccional (padding, margin...) llega a aplicarse.
.customer-profile ::v-deep .table-head th {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 1px;
  text-transform: uppercase;
  color: $text-muted;
  border-top: none;
  border-bottom: 1px solid $border;
  padding: 10px 16px;
  background: transparent;
}

.customer-profile ::v-deep .table-row td {
  padding: 12px 16px;
  vertical-align: middle;
  font-size: 13px;
}

.cell-mono {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text;
}

.cell-total {
  font-weight: 700;
}

.cell-descuento {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  color: $danger;
  margin-left: 6px;
}

.badge-estado {
  padding: 3px 10px;
  border-radius: 20px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 700;

  &--ok { background: rgba($success, 0.1); color: $success; }
  &--no { background: rgba($text-muted, 0.1); color: $text-muted; }
  &--warn { background: rgba(#F59E0B, 0.1); color: #F59E0B; }
  &--click { cursor: pointer; }
}

.cell-pago-detalle {
  display: block;
  font-size: 10px;
  color: $text-muted;
  margin-top: 2px;
}

.cell-actions {
  display: flex;
  gap: 4px;
}

.action-btn {
  width: 30px;
  height: 30px;
  border-radius: 6px;
  border: 1px solid transparent;
  background: transparent;
  color: $text-muted;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  transition: all 0.15s ease;

  &--view:hover { background: rgba($primary, 0.1); color: $primary; }
  &--edit:hover { background: rgba($primary, 0.1); color: $primary; }
  &--pagos:hover { background: rgba(#F59E0B, 0.1); color: #F59E0B; }
  &--proforma:hover { background: rgba(#06B6D4, 0.1); color: #06B6D4; }
  &--pdf:hover { background: rgba($danger, 0.1); color: $danger; }
}

.footer-count {
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  color: $text-muted;
}

.filter-pills {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.pill {
  padding: 4px 12px;
  border-radius: 20px;
  border: 1px solid $border;
  background: transparent;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 500;
  color: $text-muted;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: $primary;
    color: $primary;
  }

  &.active {
    background: $primary;
    border-color: $primary;
    color: white;
  }
}

.pedido-items {
  border-top: 1px solid $border;
  padding: 10px 16px;
  background: $bg;

  &__row {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 6px 0;
  }

  &__img {
    width: 36px;
    height: 36px;
    object-fit: cover;
    border-radius: 6px;
    border: 1px solid $border;
  }

  &__info {
    flex: 1;
    min-width: 0;
  }

  &__title {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: $text;
  }

  &__sku {
    font-family: 'JetBrains Mono', monospace;
    font-size: 11px;
    color: $text-muted;
  }
}

@media (max-width: 768px) {
  .kpi-strip { grid-template-columns: repeat(2, 1fr); }
}

// Dark mode
.dark-layout {
  .customer-profile {
    .header-title { color: #E2E8F0; }
    .btn-back { background: #1E293B; border-color: #334155; }

    .kpi-card, .panel-card { background: #1E293B; border-color: #334155; }
    .kpi-value { color: #F1F5F9; }
    .panel-card__header { border-color: #334155; }
    .panel-card__title { color: #E2E8F0; }

    .detail-item:hover { background: rgba(255,255,255,0.03); }
    .detail-item__icon { background: #334155; color: #94A3B8; }
    .detail-item__value { color: #E2E8F0; }

    .table-head th { color: #94A3B8; border-color: #334155; }
    .table-row td { border-color: #334155; }
    .cell-mono { color: #E2E8F0; }

    .pill {
      border-color: #475569;
      color: #94A3B8;
      &:hover { border-color: #3B82F6; color: #3B82F6; }
      &.active { background: #3B82F6; border-color: #3B82F6; color: white; }
    }

    .pedido-items { background: rgba(255,255,255,0.02); border-color: #334155; }
    .pedido-items__title { color: #E2E8F0; }
    .pedido-items__img { border-color: #334155; }

    .b2b-body { border-color: #334155; }
    .b2b-invite { background: rgba(255,255,255,0.02); border-color: #334155; }
  }
}
</style>
